/**
 * @file Mapbox-helper.ts
 * @module MapboxHelper
 * @description Utilītfunkcijas MVT slāņu stilu definīciju apstrādei atsevišķos
 *              līniju-stilu un punktu-stilu masīvos. Apstrādā stilu īpašību
 *              normalizēšanu (tālummaiņas diapazoni, caurspīdīgums, krāsas)
 *              un piktogrammu URL atrisināšanu. Stilu dati nāk no Kartes
 *              vizualizācijas pārvaldības konfigurācijas.
 */

import { IS_LOCALHOST } from "../map/Endpoints";
import { LayerStyle, LineStyle, PointStyle } from "../store/Layers.repository";

/**
 * Extracts and flattens line and point styles from layer style definitions.
 * Enriches each style with layer-level metadata (zoom range, opacity, source-layer name).
 *
 * @param {LayerStyle[]} styleLayers - Array of layer style definitions from backend
 * @param {boolean} [merged] - If true, use "public." prefix + original table name for source-layer
 * @returns {[LineStyle[], PointStyle[]]} Tuple of [lineStyles, pointStyles] ready for Mapbox generation
 */
export const getMVTStyles = (styleLayers: LayerStyle[], merged?: boolean): [LineStyle[], PointStyle[]] => {
	const lines: LineStyle[] = styleLayers
		.map((l: LayerStyle) =>
			l.lines.map((line) => ({
				...line,
				layerName: l.name,
				groupName: l.groupName,
				maxZoom: l.zoom_to,
				minZoom: l.zoom_from,
				opacity: l.opacity ? (100 - l.opacity) / 100 : 1,
				source: merged ? "public." + (l.original_table ?? l.table) : `${!IS_LOCALHOST && l.mvt_url ? "public." : ""}${l.table}`,
				attributes: l.attributes.split(","),
				label_style: l.label_style,
				label: l.label,
				use_uvis_popups: l.use_uvis_popups,
			}))
		)
		.flat();
	const points: PointStyle[] = styleLayers
		.map((l: LayerStyle) =>
			l.points.map((point) => ({
				...point,
				layerName: l.name,
				groupName: l.groupName,
				maxZoom: l.zoom_to,
				minZoom: l.zoom_from,
				opacity: l.opacity ? (100 - l.opacity) / 100 : 1,
				source: merged ? "public." + (l.original_table ?? l.table) : `${!IS_LOCALHOST && l.mvt_url ? "public." : ""}${l.table}`,
				attributes: l.attributes.split(","),
				label_style: l.label_style,
				label: l.label,
				use_uvis_popups: l.use_uvis_popups,
			}))
		)
		.flat();
	return [lines, points];
};
